import React, { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import { getApiErrorMessage } from '../shared/utils/apiError'
import { Card, CardContent, CardHeader, CardTitle, CardDescription, Button, Badge, Dialog, DialogContent, DialogHeader, DialogTitle, Input, Label, Textarea } from '@jira-clone/ui'
import { Plus, FolderKanban, Layers, CheckSquare, Search, ArrowLeft } from 'lucide-react'
import { cn, cardClassName } from '@jira-clone/ui'

interface Project {
  id: string
  name: string
  key: string
  color: string
  status: string
  description?: string
  tasksCount: number
}

export default function ProjectsPage() {
  const navigate = useNavigate()
  const { organization } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  
  const urlProjectId = searchParams.get('projectId')
  const [search, setSearch] = useState('')
  
  // Dialog state
  const [isOpen, setIsOpen] = useState(false)
  const [name, setName] = useState('')
  const [key, setKey] = useState('')
  const [description, setDescription] = useState('')
  const [color, setColor] = useState('#6366f1')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  // Fetch projects
  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['projects', organization?.id],
    queryFn: () => api.get<Project[]>(`/organizations/${organization?.id}/projects`),
    enabled: !!organization?.id,
  })

  useEffect(() => {
    const handleCreated = () => {
      queryClient.invalidateQueries({ queryKey: ['projects', organization?.id] })
    }
    window.addEventListener('projectCreated', handleCreated)
    return () => window.removeEventListener('projectCreated', handleCreated)
  }, [organization?.id, queryClient])

  // Create project mutation
  const createProjectMutation = useMutation({
    mutationFn: (data: { name: string; key: string; description?: string; color: string; organizationId: string }) =>
      api.post('/projects', data, { skipErrorToast: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', organization?.id] })
      setName('')
      setKey('')
      setDescription('')
      setCreateError(null)
      setIsOpen(false)
      window.dispatchEvent(new CustomEvent('projectCreated'))
    },
    onError: (err: any) => {
      toast.error(getApiErrorMessage(err, 'create projects'))
    },
    onSettled: () => setCreating(false),
  })

  const filteredProjects = projects.filter(
    (p) =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.key.toLowerCase().includes(search.toLowerCase())
  )

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    setCreateError(null)

    if (!name.trim()) {
      setCreateError('Please enter a project name')
      return
    }

    const trimmedKey = (key || name.replace(/[^a-zA-Z]/g, '').slice(0, 4)).trim().toUpperCase()
    if (!trimmedKey) {
      setCreateError('Please enter a project key (e.g. PRJ)')
      return
    }

    if (!organization?.id) {
      setCreateError('Active workspace not found. Please ensure you are logged in.')
      return
    }

    setCreating(true)
    createProjectMutation.mutate({
      name: name.trim(),
      key: trimmedKey,
      description: description.trim() || undefined,
      color,
      organizationId: organization.id,
    })
  }

  return (
    <div className="space-y-6 w-full max-w-[1800px] mx-auto">
      {/* Header bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200 transition-all hover:shadow-md hover:shadow-purple-300/50"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-foreground">Projects</h2>
            <p className="text-sm text-muted-foreground">Manage and organize your team workspaces</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative w-64">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search projects..."
              className="pl-9 h-9 text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button onClick={() => setIsOpen(true)} className="gap-1.5 h-9">
            <Plus className="h-4 w-4" />
            <span>New Project</span>
          </Button>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {filteredProjects.map((project) => (
          <Card key={project.id} className="transition-all flex flex-col justify-between" interactive>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <span
                  className="flex h-10 w-10 items-center justify-center rounded-xl text-white font-bold text-sm shadow-sm"
                  style={{ backgroundColor: project.color || '#6366f1' }}
                >
                  {project.key.slice(0, 3)}
                </span>
                <Badge variant="outline" className="uppercase text-[10px]">
                  {project.status || 'Active'}
                </Badge>
              </div>
              <CardTitle className="text-lg font-bold mt-3 text-foreground">{project.name}</CardTitle>
              <CardDescription className="line-clamp-2 min-h-[40px] text-xs">
                {project.description || 'No description provided'}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-2 border-t border-border flex flex-col gap-3">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <CheckSquare className="h-3.5 w-3.5" />
                  <span>{project.tasksCount || 0} tasks</span>
                </div>
                <span className="font-mono font-semibold">{project.key}</span>
              </div>

              {/* Quick Route Buttons */}
              <div className="grid grid-cols-3 gap-1.5 pt-1">
                <Button
                  asChild
                  variant="secondary"
                  size="sm"
                  className="h-8 text-[11px] px-2"
                >
                  <Link to={`/spaces/${project.id}/list`}>
                    List
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="secondary"
                  size="sm"
                  className="h-8 text-[11px] px-2"
                >
                  <Link to={`/spaces/${project.id}/board`}>
                    Board
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="secondary"
                  size="sm"
                  className="h-8 text-[11px] px-2"
                >
                  <Link to={`/spaces/${project.id}/backlog`}>
                    Backlog
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredProjects.length === 0 && !isLoading && (
          <div className={cn(cardClassName(), 'col-span-full text-center py-16')}>
            <FolderKanban className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-40" />
            <h3 className="font-semibold text-foreground">No projects found</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">
              Get started by creating your first project workspace.
            </p>
            <Button onClick={() => setIsOpen(true)} size="sm">
              <Plus className="h-4 w-4 mr-1" /> Create Project
            </Button>
          </div>
        )}
      </div>

      {/* Create Project Modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            {createError && (
              <div className="p-3 text-xs text-red-600 bg-red-50 dark:bg-red-950/40 rounded-lg border border-red-200 dark:border-red-900">
                {createError}
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="projName">Project Name</Label>
              <Input
                id="projName"
                placeholder="Marketing Campaign 2026"
                required
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                  if (!key) {
                    setKey(e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 4).toUpperCase())
                  }
                }}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="projKey">Project Key (Short Code)</Label>
              <Input
                id="projKey"
                placeholder="MKT"
                required
                maxLength={6}
                value={key}
                onChange={(e) => setKey(e.target.value.toUpperCase())}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="projDesc">Description (Optional)</Label>
              <Textarea
                id="projDesc"
                placeholder="Describe project objectives and scope..."
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="projColor">Color Badge</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  id="projColor"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-9 w-12 rounded cursor-pointer border border-input"
                />
                <span className="text-xs font-mono text-muted-foreground">{color}</span>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={creating}>
                Create Project
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}